import { useRef, useState } from 'react';
import {
  Button,
  ColorField,
  FieldShell,
  IconButton,
  SelectField,
  SwitchField,
  TextAreaField,
  TextField,
} from '@tb/ui';
import type { AssetUploadResponse, TemplateField, TemplateManifest } from '@tb/shared';
import { useT, type Translate } from '../../i18n/LocaleProvider';
import { useManifestText } from '../../i18n/useManifestText';

type ManifestText = ReturnType<typeof useManifestText>;

export interface TemplateFormProps {
  manifest: TemplateManifest;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  assets: AssetUploadResponse[];
  onRequestUpload: () => void;
  onUpload: (file: File) => Promise<AssetUploadResponse | null>;
}

export function TemplateForm({
  manifest,
  value,
  onChange,
  assets,
  onRequestUpload,
  onUpload,
}: TemplateFormProps) {
  const t = useT();
  const m = useManifestText();

  return (
    <div className="tb-stack" style={{ gap: 18 }}>
      {manifest.fields.map((field) => (
        <FieldRenderer
          key={field.key}
          field={field}
          value={value[field.key]}
          onChange={(next) => onChange({ ...value, [field.key]: next })}
          assets={assets}
          onRequestUpload={onRequestUpload}
          onUpload={onUpload}
          t={t}
          m={m}
        />
      ))}
    </div>
  );
}

interface FieldRendererProps {
  field: TemplateField;
  value: unknown;
  onChange: (value: unknown) => void;
  assets: AssetUploadResponse[];
  onRequestUpload: () => void;
  onUpload: (file: File) => Promise<AssetUploadResponse | null>;
  t: Translate;
  m: ManifestText;
}

function FieldRenderer({
  field,
  value,
  onChange,
  assets,
  onRequestUpload,
  onUpload,
  t,
  m,
}: FieldRendererProps) {
  const label = m(field.label);
  const hint = field.hint ? m(field.hint) : undefined;
  const placeholder = field.placeholder ? m(field.placeholder) : undefined;

  switch (field.type) {
    case 'text':
      return (
        <TextAreaField
          label={label}
          required={field.required}
          hint={hint}
          placeholder={placeholder}
          maxLength={field.max}
          value={asString(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case 'boolean':
      return <SwitchField label={label} hint={hint} checked={value === true} onChange={onChange} />;

    case 'color':
      return (
        <ColorField
          label={label}
          hint={hint}
          value={asString(value) || asString(field.default)}
          onChange={onChange}
        />
      );

    case 'select':
      return (
        <SelectField
          label={label}
          required={field.required}
          hint={hint}
          options={(field.options ?? []).map((option) => ({
            value: option.value,
            label: m(option.label),
          }))}
          placeholder={field.required ? undefined : t('form.notSet')}
          value={asString(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case 'number':
      return (
        <TextField
          label={label}
          required={field.required}
          hint={hint}
          type="number"
          inputMode="decimal"
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(event) =>
            onChange(event.target.value === '' ? undefined : Number(event.target.value))
          }
        />
      );

    case 'image':
      return (
        <ImageField
          label={label}
          hint={hint}
          required={field.required}
          value={asString(value)}
          onChange={onChange}
          assets={assets}
          onRequestUpload={onRequestUpload}
          onUpload={onUpload}
          t={t}
        />
      );

    case 'icon':
      return (
        <IconField
          label={label}
          hint={hint}
          options={(field.options ?? []).map((option) => ({
            value: option.value,
            label: m(option.label),
          }))}
          value={asString(value)}
          onChange={onChange}
        />
      );

    case 'array':
      return (
        <ArrayField
          field={field}
          label={label}
          hint={hint}
          value={Array.isArray(value) ? (value as Record<string, unknown>[]) : []}
          onChange={onChange}
          assets={assets}
          onRequestUpload={onRequestUpload}
          onUpload={onUpload}
          t={t}
          m={m}
        />
      );

    default:
      return (
        <TextField
          label={label}
          required={field.required}
          hint={hint}
          placeholder={placeholder}
          maxLength={field.max}
          type={inputType(field.type)}
          inputMode={field.type === 'tel' ? 'tel' : undefined}
          autoCapitalize={field.type === 'url' || field.type === 'email' ? 'none' : undefined}
          spellCheck={field.type === 'url' || field.type === 'address' ? false : undefined}
          value={asString(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}

function ImageField({
  label,
  hint,
  required,
  value,
  onChange,
  assets,
  onRequestUpload,
  onUpload,
  t,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  value: string;
  onChange: (value: unknown) => void;
  assets: AssetUploadResponse[];
  onRequestUpload: () => void;
  onUpload: (file: File) => Promise<AssetUploadResponse | null>;
  t: Translate;
}) {
  const selected = assets.find((asset) => asset.id === value);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file: File) => {
    setBusy(true);
    try {
      const asset = await onUpload(file);
      if (asset) onChange(asset.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FieldShell label={label} required={required} hint={hint}>
      <div className="tb-stack">
        {selected ? (
          <div className="tb-row">
            <div className="asset-tile" style={{ width: 64, height: 64, aspectRatio: 'auto' }}>
              <img src={selected.previewUrl} alt="" />
            </div>
            <span className="tb-grow tb-truncate tb-hint">{selected.path}</span>
            <IconButton
              icon="close"
              label={t('form.removeImage')}
              onClick={() => onChange(undefined)}
            />
          </div>
        ) : null}

        {assets.length > 0 ? (
          <div className="asset-grid">
            {assets.map((asset) => (
              <button
                key={asset.id}
                type="button"
                className="asset-tile"
                aria-pressed={asset.id === value}
                style={asset.id === value ? { outline: '2px solid var(--tb-accent)' } : undefined}
                onClick={() => onChange(asset.id)}
              >
                <img src={asset.previewUrl} alt={asset.path} />
              </button>
            ))}
          </div>
        ) : null}

        <input
          ref={input}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void pick(file);
          }}
        />

        <div className="tb-row">
          <Button
            size="sm"
            variant="secondary"
            icon="upload"
            loading={busy}
            onClick={() => input.current?.click()}
          >
            {t('editor.files.uploadImage')}
          </Button>
          <Button size="sm" variant="ghost" icon="folder" onClick={onRequestUpload}>
            {t('form.allFiles')}
          </Button>
        </div>
      </div>
    </FieldShell>
  );
}

function IconField({
  label,
  hint,
  options,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: unknown) => void;
}) {
  if (options.length === 0) {
    return (
      <TextField
        label={label}
        hint={hint}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  return (
    <FieldShell label={label} hint={hint}>
      <div className="icon-picker">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className="icon-picker__option"
            aria-pressed={option.value === value}
            title={option.label}
            onClick={() => onChange(option.value === value ? undefined : option.value)}
          >
            <span className="tb-hint" style={{ fontSize: 10 }}>
              {option.label.slice(0, 4)}
            </span>
          </button>
        ))}
      </div>
    </FieldShell>
  );
}

function ArrayField({
  field,
  label,
  hint,
  value,
  onChange,
  assets,
  onRequestUpload,
  onUpload,
  t,
  m,
}: {
  field: TemplateField;
  label: string;
  hint?: string;
  value: Record<string, unknown>[];
  onChange: (value: unknown) => void;
  assets: AssetUploadResponse[];
  onRequestUpload: () => void;
  onUpload: (file: File) => Promise<AssetUploadResponse | null>;
  t: Translate;
  m: ManifestText;
}) {
  const children = field.of ?? [];
  const maxItems = field.maxItems ?? 50;

  const update = (index: number, entry: Record<string, unknown>) => {
    const next = [...value];
    next[index] = entry;
    onChange(next);
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item!);
    onChange(next);
  };

  return (
    <div className="tb-stack">
      <div className="tb-row tb-row--between">
        <span className="tb-field__label">
          {label}
          {field.required ? <span className="tb-field__required">*</span> : null}
        </span>
        <span className="tb-hint">
          {value.length} / {maxItems}
        </span>
      </div>
      {hint ? <span className="tb-hint">{hint}</span> : null}

      {value.map((entry, index) => (
        <div className="array-item" key={index}>
          <div className="array-item__head">
            <span className="tb-hint">#{index + 1}</span>
            <div className="tb-row" style={{ gap: 2 }}>
              <IconButton
                icon="chevron-down"
                label={t('form.moveDown')}
                onClick={() => move(index, 1)}
                disabled={index === value.length - 1}
              />
              <IconButton
                icon="chevron-down"
                label={t('form.moveUp')}
                style={{ transform: 'rotate(180deg)' }}
                onClick={() => move(index, -1)}
                disabled={index === 0}
              />
              <IconButton
                icon="trash"
                label={t('form.remove')}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              />
            </div>
          </div>

          {children.map((child) => (
            <FieldRenderer
              key={child.key}
              field={child}
              value={entry[child.key]}
              onChange={(next) => update(index, { ...entry, [child.key]: next })}
              assets={assets}
              onRequestUpload={onRequestUpload}
              onUpload={onUpload}
              t={t}
              m={m}
            />
          ))}
        </div>
      ))}

      <Button
        size="sm"
        variant="secondary"
        icon="plus"
        disabled={value.length >= maxItems}
        onClick={() => onChange([...value, blankEntry(children)])}
      >
        {t('form.add')}
      </Button>
    </div>
  );
}

function blankEntry(children: TemplateField[]): Record<string, unknown> {
  const entry: Record<string, unknown> = {};
  for (const child of children) {
    if (child.default !== undefined) entry[child.key] = child.default;
  }
  return entry;
}

function inputType(type: TemplateField['type']): string {
  switch (type) {
    case 'url':
      return 'url';
    case 'email':
      return 'email';
    case 'tel':
      return 'tel';
    default:
      return 'text';
  }
}

function asString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}
